import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Home,
  Users,
  CreditCard,
  BellRing,
  FileBarChart,
  ClipboardList,
  User,
  Users as UsersIcon,
  Rocket,
  Wrench,
  ChevronRight,
  ArrowLeft,
  BookOpen,
} from 'lucide-react';
import {
  helpArticles,
  categories,
  searchHelp,
  getArticlesByCategory,
  getRelatedArticles,
  type HelpArticle,
  type HelpCategory,
} from '../../data/helpContent';
import { APP_VERSION, BUILD_DATE } from '../../version';

// Icon mapping
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  RocketLaunchIcon: Rocket,
  HomeIcon: Home,
  UserGroupIcon: Users,
  CreditCardIcon: CreditCard,
  UserIcon: User,
  UsersIcon: UsersIcon,
  BellAlertIcon: BellRing,
  DocumentChartBarIcon: FileBarChart,
  ClipboardDocumentListIcon: ClipboardList,
  WrenchScrewdriverIcon: Wrench,
};

function CategoryIcon({ iconName, className }: { iconName: string; className?: string }) {
  const Icon = iconMap[iconName] || BookOpen;
  return <Icon className={className} />;
}

interface HelpPageProps {}

export default function HelpPage(_props: HelpPageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<HelpCategory | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<HelpArticle | null>(null);

  // Search results
  const searchResults = useMemo(() => {
    if (searchQuery.trim().length < 2) return null;
    return searchHelp(searchQuery);
  }, [searchQuery]);

  // Category articles
  const categoryArticles = useMemo(() => {
    if (!selectedCategory) return null;
    return getArticlesByCategory(selectedCategory);
  }, [selectedCategory]);

  // Handle article click
  const handleArticleClick = (article: HelpArticle) => {
    setSelectedArticle(article);
    setSearchQuery('');
  };

  // Handle category click
  const handleCategoryClick = (category: HelpCategory) => {
    setSelectedCategory(category);
    setSelectedArticle(null);
    setSearchQuery('');
  };

  // Handle back navigation
  const handleBack = () => {
    if (selectedArticle) {
      setSelectedArticle(null);
    } else if (selectedCategory) {
      setSelectedCategory(null);
    }
    setSearchQuery('');
  };

  // Render article content with markdown-like formatting
  const renderContent = (content: string) => {
    const lines = content.split('\n');
    const elements: JSX.Element[] = [];
    let inList = false;
    let listItems: string[] = [];

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <ul key={`list-${elements.length}`} className="list-disc list-inside space-y-1 mb-4 text-muted-foreground">
            {listItems.map((item, i) => (
              <li key={i} dangerouslySetInnerHTML={{ __html: formatInline(item) }} />
            ))}
          </ul>
        );
        listItems = [];
      }
      inList = false;
    };

    const formatInline = (text: string): string => {
      // Bold text **text**
      text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      // Code `text`
      text = text.replace(/`(.+?)`/g, '<code class="bg-muted px-1 py-0.5 rounded text-sm">$1</code>');
      return text;
    };

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();

      // Empty line
      if (trimmedLine === '') {
        flushList();
        return;
      }

      // Header
      if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**') && !trimmedLine.includes(':')) {
        flushList();
        const headerText = trimmedLine.slice(2, -2);
        elements.push(
          <h3 key={index} className="text-lg font-semibold text-foreground mt-6 mb-3">
            {headerText}
          </h3>
        );
        return;
      }

      // List item
      if (trimmedLine.startsWith('- ')) {
        inList = true;
        listItems.push(trimmedLine.slice(2));
        return;
      }

      // Numbered list
      if (/^\d+\.\s/.test(trimmedLine)) {
        if (!inList) {
          inList = true;
        }
        listItems.push(trimmedLine.replace(/^\d+\.\s/, ''));
        return;
      }

      // Regular paragraph
      flushList();
      elements.push(
        <p key={index} className="mb-4 text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: formatInline(trimmedLine) }} />
      );
    });

    flushList();
    return elements;
  };

  // Render article view
  if (selectedArticle) {
    const relatedArticles = getRelatedArticles(selectedArticle);
    const categoryInfo = categories.find(c => c.id === selectedArticle.category);

    return (
      <div className="max-w-4xl mx-auto">
        {/* Breadcrumb */}
        <div className="mb-6">
          <button
            onClick={handleBack}
            className="inline-flex items-center text-sm text-muted-foreground hover:text-primary-600 dark:hover:text-primary-400"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to {selectedCategory ? categoryInfo?.name : 'Help'}
          </button>
        </div>

        {/* Article */}
        <article className="card p-6 md:p-8">
          <div className="mb-6">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300 mb-3">
              <CategoryIcon iconName={categoryInfo?.icon || 'BookOpenIcon'} className="h-3 w-3 mr-1" />
              {categoryInfo?.name}
            </span>
            <h1 className="text-2xl font-bold text-foreground">
              {selectedArticle.title}
            </h1>
            <p className="mt-2 text-muted-foreground">
              {selectedArticle.summary}
            </p>
          </div>

          <div className="prose dark:prose-invert max-w-none">
            {renderContent(selectedArticle.content)}
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <div className="mt-8 pt-6 border-t border-border">
              <h4 className="text-sm font-semibold text-foreground mb-3">
                Related Articles
              </h4>
              <div className="flex flex-wrap gap-2">
                {relatedArticles.map(article => (
                  <button
                    key={article.id}
                    onClick={() => handleArticleClick(article)}
                    className="inline-flex items-center px-3 py-1.5 rounded-lg bg-muted text-sm text-foreground hover:bg-primary-100 dark:hover:bg-primary-900 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                  >
                    <ChevronRight className="h-3 w-3 mr-1" />
                    {article.title}
                  </button>
                ))}
              </div>
            </div>
          )}
        </article>
      </div>
    );
  }

  // Render category view
  if (selectedCategory) {
    const categoryInfo = categories.find(c => c.id === selectedCategory)!;

    return (
      <div className="max-w-4xl mx-auto">
        {/* Breadcrumb */}
        <div className="mb-6">
          <button
            onClick={handleBack}
            className="inline-flex items-center text-sm text-muted-foreground hover:text-primary-600 dark:hover:text-primary-400"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Help
          </button>
        </div>

        {/* Category Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-400">
              <CategoryIcon iconName={categoryInfo.icon} className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {categoryInfo.name}
              </h1>
              <p className="text-muted-foreground">
                {categoryInfo.description}
              </p>
            </div>
          </div>
        </div>

        {/* Articles in Category */}
        <div className="space-y-3">
          {categoryArticles?.map(article => (
            <button
              key={article.id}
              onClick={() => handleArticleClick(article)}
              className="w-full card p-4 text-left hover:shadow-md transition-shadow group"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-foreground group-hover:text-primary-600 dark:group-hover:text-primary-400">
                    {article.title}
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
                    {article.summary}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary-600 dark:group-hover:text-primary-400 flex-shrink-0" />
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Render main help page
  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 text-white mb-4">
          <BookOpen className="h-8 w-8" />
        </div>
        <h1 className="text-3xl font-bold text-foreground">
          Help Center
        </h1>
        <p className="mt-2 text-muted-foreground">
          Find answers and learn how to use the Pax8 Licensing Portal
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Version {APP_VERSION} | Last updated: {BUILD_DATE}
        </p>
      </div>

      {/* Search */}
      <div className="mb-8">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search help articles..."
            className="w-full pl-12 pr-4 py-3 border border-border rounded-xl bg-card text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent shadow-sm"
          />
        </div>

        {/* Search Results */}
        {searchResults && (
          <div className="mt-4 card p-4">
            <p className="text-sm text-muted-foreground mb-3">
              {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{searchQuery}"
            </p>
            {searchResults.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">
                No articles found. Try a different search term.
              </p>
            ) : (
              <div className="space-y-2">
                {searchResults.slice(0, 8).map(article => {
                  const cat = categories.find(c => c.id === article.category);
                  return (
                    <button
                      key={article.id}
                      onClick={() => handleArticleClick(article)}
                      className="w-full p-3 text-left rounded-lg hover:bg-accent transition-colors group"
                    >
                      <div className="flex items-start gap-3">
                        <CategoryIcon iconName={cat?.icon || 'BookOpenIcon'} className="h-5 w-5 text-muted-foreground mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-foreground group-hover:text-primary-600 dark:group-hover:text-primary-400">
                            {article.title}
                          </h4>
                          <p className="text-sm text-muted-foreground line-clamp-1">
                            {article.summary}
                          </p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </button>
                  );
                })}
                {searchResults.length > 8 && (
                  <p className="text-sm text-muted-foreground text-center pt-2">
                    And {searchResults.length - 8} more results...
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Categories Grid */}
      {!searchResults && (
        <>
          <h2 className="text-lg font-semibold text-foreground mb-4">
            Browse by Category
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            {categories.map(category => {
              const articleCount = getArticlesByCategory(category.id).length;
              return (
                <button
                  key={category.id}
                  onClick={() => handleCategoryClick(category.id)}
                  className="card p-4 text-left hover:shadow-md hover:border-primary-300 dark:hover:border-primary-700 transition-all group"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-2.5 rounded-lg bg-muted text-muted-foreground group-hover:bg-primary-100 dark:group-hover:bg-primary-900 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                      <CategoryIcon iconName={category.icon} className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-foreground group-hover:text-primary-600 dark:group-hover:text-primary-400">
                        {category.name}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        {category.description}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {articleCount} article{articleCount !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary-600 dark:group-hover:text-primary-400" />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Popular Articles */}
          <h2 className="text-lg font-semibold text-foreground mb-4">
            Popular Articles
          </h2>
          <div className="space-y-3">
            {helpArticles.slice(0, 6).map(article => {
              const cat = categories.find(c => c.id === article.category);
              return (
                <button
                  key={article.id}
                  onClick={() => handleArticleClick(article)}
                  className="w-full card p-4 text-left hover:shadow-md transition-shadow group"
                >
                  <div className="flex items-start gap-3">
                    <CategoryIcon iconName={cat?.icon || 'BookOpenIcon'} className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-foreground group-hover:text-primary-600 dark:group-hover:text-primary-400">
                        {article.title}
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-1">
                        {article.summary}
                      </p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary-600 dark:group-hover:text-primary-400" />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-border text-center">
            <p className="text-sm text-muted-foreground">
              Can't find what you're looking for?{' '}
              <Link to="/changelog" className="text-primary-600 dark:text-primary-400 hover:underline">
                Check the Changelog
              </Link>{' '}
              for the latest updates.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
